/**
 * Administrator panel of the category rail (TASK-0052).
 *
 * The taxonomy is two levels deep — a category and the subcategories beneath it — and the panel renders exactly that:
 * a list of categories, and, for the one the administrator opened, the subcategories it holds. Rendering both levels at
 * once would mean a list of lists whose actions are ambiguous, so only one category is expanded at a time.
 *
 * Three rules of the lifecycle are rendered rather than hidden:
 *
 * - **a slug is written once.** The create forms take it; the edit forms do not, because the document identifier is
 *   derived from the slug and stored prompts refer to it — a rename must never move a reference;
 * - **deleting is soft, and restoring comes back disabled.** A removed row stays visible (the admin read includes
 *   deleted rows) so it can be restored, and restoring leaves it inactive, which makes re-offering it a second click;
 * - **a category cannot be deleted while it holds a live subcategory.** The child count is on the row, so the panel
 *   explains the refusal and offers to open the children instead of firing a request the API will reject.
 */

import { Check, ChevronDown, ChevronLeft, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Input, LtrInput } from '@/components/ui/input';
import {
  Alert,
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Spinner,
} from '@/components/ui/surface';
import type { AdminCategory, AdminSubCategory } from '@/features/admin/api/adminCategoryApi';
import {
  useAdminCategories,
  useAdminSubCategories,
  useCreateCategory,
  useCreateSubCategory,
  useDeleteCategory,
  useDeleteSubCategory,
  useRestoreCategory,
  useRestoreSubCategory,
  useSetCategoryState,
  useSetSubCategoryState,
  useUpdateCategory,
  useUpdateSubCategory,
} from '@/features/admin/hooks/useAdminCategories';
import { useProblemMessage } from '@/lib/errors';

/** One category draft, held as strings so a half-typed number is never coerced behind the user's back. */
interface CategoryDraft {
  slug: string;
  nameFa: string;
  nameEn: string;
  iconName: string;
  order: string;
  isActive: boolean;
}

/** One subcategory draft, held as strings for the same reason. */
interface SubCategoryDraft {
  slug: string;
  nameFa: string;
  nameEn: string;
  order: string;
  isActive: boolean;
}

/** The empty category draft a create form starts from. */
function emptyCategoryDraft(): CategoryDraft {
  return { slug: '', nameFa: '', nameEn: '', iconName: '', order: '100', isActive: true };
}

/** The empty subcategory draft a create form starts from. */
function emptySubCategoryDraft(): SubCategoryDraft {
  return { slug: '', nameFa: '', nameEn: '', order: '100', isActive: true };
}

/** Projects a stored category onto an editable draft; the slug is carried for the identifier, not for editing. */
function categoryDraftOf(category: AdminCategory): CategoryDraft {
  return {
    slug: category.slug,
    nameFa: category.nameFa,
    nameEn: category.nameEn,
    iconName: category.iconName ?? '',
    order: String(category.order),
    isActive: category.isActive,
  };
}

/** Projects a stored subcategory onto an editable draft. */
function subCategoryDraftOf(subCategory: AdminSubCategory): SubCategoryDraft {
  return {
    slug: subCategory.slug,
    nameFa: subCategory.nameFa,
    nameEn: subCategory.nameEn,
    order: String(subCategory.order),
    isActive: subCategory.isActive,
  };
}

/** Reads an order field that may have been left empty; an unreadable value keeps the default. */
function orderOf(value: string, fallback = 100): number {
  const parsed = Number(value.trim());

  return Number.isFinite(parsed) ? parsed : fallback;
}

/** Whether a create form holds the two values the API insists on. */
function isCreatable(slug: string, nameFa: string, nameEn: string): boolean {
  return slug.trim().length > 0 && nameFa.trim().length > 0 && nameEn.trim().length > 0;
}

/** Renders the category list, its create form and the expanded category's subcategories. */
export function CategoryAdmin() {
  const { t } = useTranslation();
  const messageFor = useProblemMessage();

  const categories = useAdminCategories(true);
  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategory();
  const setCategoryState = useSetCategoryState();
  const removeCategory = useDeleteCategory();
  const restoreCategory = useRestoreCategory();

  const [draft, setDraft] = useState<CategoryDraft>(emptyCategoryDraft);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const rows = categories.data ?? [];

  const submitCreate = () => {
    createCategory.mutate(
      {
        slug: draft.slug.trim(),
        nameFa: draft.nameFa.trim(),
        nameEn: draft.nameEn.trim(),
        order: orderOf(draft.order),
        iconName: draft.iconName.trim().length === 0 ? null : draft.iconName.trim(),
        isActive: draft.isActive,
      },
      { onSuccess: () => setDraft(emptyCategoryDraft()) },
    );
  };

  const mutationError =
    createCategory.error ??
    updateCategory.error ??
    setCategoryState.error ??
    removeCategory.error ??
    restoreCategory.error ??
    null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('admin.categories.title')}</CardTitle>
        <CardDescription>{t('admin.categories.subtitle')}</CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        {categories.isPending ? (
          <div className="flex justify-center p-4">
            <Spinner />
          </div>
        ) : null}

        {categories.isError ? (
          <Alert variant="destructive">{messageFor(categories.error)}</Alert>
        ) : null}
        {mutationError === null ? null : (
          <Alert variant="destructive">{messageFor(mutationError)}</Alert>
        )}

        {rows.length === 0 && !categories.isPending ? (
          <p className="text-sm text-muted-foreground">{t('admin.categories.empty')}</p>
        ) : null}

        <ul className="flex flex-col gap-2">
          {rows.map((category) => (
            <li key={category.id} className="rounded-lg border border-border p-3">
              <CategoryRow
                category={category}
                expanded={expandedId === category.id}
                onToggleExpanded={() =>
                  setExpandedId((current) => (current === category.id ? null : category.id))
                }
                onSave={(payload) => updateCategory.mutate({ categoryId: category.id, payload })}
                isSaving={updateCategory.isPending}
                onToggleState={() =>
                  setCategoryState.mutate({
                    categoryId: category.id,
                    isActive: !category.isActive,
                  })
                }
                isTogglingState={setCategoryState.isPending}
                onDelete={() => removeCategory.mutate(category.id)}
                isDeleting={removeCategory.isPending}
                onRestore={() => restoreCategory.mutate(category.id)}
                isRestoring={restoreCategory.isPending}
              />

              {expandedId === category.id ? (
                <SubCategoryPanel categoryId={category.id} categoryDeleted={category.isDeleted} />
              ) : null}
            </li>
          ))}
        </ul>

        <CategoryCreateForm
          draft={draft}
          onChange={setDraft}
          onSubmit={submitCreate}
          isSaving={createCategory.isPending}
        />
      </CardContent>
    </Card>
  );
}

/** Props accepted by {@link CategoryCreateForm}. */
interface CategoryCreateFormProps {
  /** Draft being filled in. */
  draft: CategoryDraft;
  /** Replaces the whole draft. */
  onChange: (draft: CategoryDraft) => void;
  /** Sends the draft. */
  onSubmit: () => void;
  /** Whether the create is in flight. */
  isSaving: boolean;
}

/**
 * The create form, as the panel's own block.
 *
 * The slug is beside the titles rather than behind a disclosure: it is the one field that cannot be corrected later, so
 * the hint under it says what it becomes (the identifier) before the administrator commits to it.
 */
function CategoryCreateForm({ draft, onChange, onSubmit, isSaving }: CategoryCreateFormProps) {
  const { t } = useTranslation();

  return (
    <div className="rounded-lg border border-dashed border-border p-3">
      <p className="mb-3 flex items-center gap-2 text-sm font-medium">
        <Plus aria-hidden className="h-4 w-4" />
        {t('admin.categories.createTitle')}
      </p>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-xs text-muted-foreground">{t('admin.categories.slug')}</span>
          <LtrInput
            value={draft.slug}
            placeholder="backend"
            onChange={(event) => onChange({ ...draft, slug: event.target.value })}
          />
          <span className="text-xs text-muted-foreground">{t('admin.categories.slugHint')}</span>
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-xs text-muted-foreground">{t('admin.categories.order')}</span>
          <LtrInput
            inputMode="numeric"
            value={draft.order}
            onChange={(event) => onChange({ ...draft, order: event.target.value })}
          />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-xs text-muted-foreground">{t('admin.categories.nameFa')}</span>
          <Input
            value={draft.nameFa}
            onChange={(event) => onChange({ ...draft, nameFa: event.target.value })}
          />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-xs text-muted-foreground">{t('admin.categories.nameEn')}</span>
          <LtrInput
            value={draft.nameEn}
            onChange={(event) => onChange({ ...draft, nameEn: event.target.value })}
          />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-xs text-muted-foreground">{t('admin.categories.iconName')}</span>
          <LtrInput
            value={draft.iconName}
            onChange={(event) => onChange({ ...draft, iconName: event.target.value })}
          />
        </label>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={draft.isActive}
            onChange={(event) => onChange({ ...draft, isActive: event.target.checked })}
          />
          {t('admin.categories.activeLabel')}
        </label>
      </div>

      <div className="mt-3">
        <Button
          type="button"
          size="sm"
          disabled={isSaving || !isCreatable(draft.slug, draft.nameFa, draft.nameEn)}
          onClick={onSubmit}
        >
          <Plus aria-hidden className="h-4 w-4" />
          {t('admin.categories.create')}
        </Button>
      </div>
    </div>
  );
}

/** Props accepted by {@link CategoryRow}. */
interface CategoryRowProps {
  /** Stored category the row renders. */
  category: AdminCategory;
  /** Whether the row's subcategories are open. */
  expanded: boolean;
  /** Opens or closes the subcategories. */
  onToggleExpanded: () => void;
  /** Saves an edit. */
  onSave: (payload: {
    nameFa: string;
    nameEn: string;
    order: number;
    iconName: string | null;
  }) => void;
  /** Whether an edit is in flight. */
  isSaving: boolean;
  /** Enables or disables the category. */
  onToggleState: () => void;
  /** Whether the state change is in flight. */
  isTogglingState: boolean;
  /** Soft-deletes the category. */
  onDelete: () => void;
  /** Whether the deletion is in flight. */
  isDeleting: boolean;
  /** Restores the category. */
  onRestore: () => void;
  /** Whether the restore is in flight. */
  isRestoring: boolean;
}

/**
 * One category: its readable metadata, its edit form and the lifecycle actions that apply to it.
 *
 * The edit form shows the slug as read-only text (under the titles) rather than as a field: the administrator should see
 * the key stored prompts carry, and should not be offered an input that would silently do nothing.
 */
function CategoryRow({
  category,
  expanded,
  onToggleExpanded,
  onSave,
  isSaving,
  onToggleState,
  isTogglingState,
  onDelete,
  isDeleting,
  onRestore,
  isRestoring,
}: CategoryRowProps) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [editDraft, setEditDraft] = useState<CategoryDraft>(() => categoryDraftOf(category));

  const startEdit = () => {
    setEditDraft(categoryDraftOf(category));
    setEditing(true);
  };

  /** Applies one field of the edit draft and sends the whole replacement, which is what the route accepts. */
  const changeEdit = (next: CategoryDraft) => {
    setEditDraft(next);
    onSave({
      nameFa: next.nameFa.trim(),
      nameEn: next.nameEn.trim(),
      order: orderOf(next.order, category.order),
      iconName: next.iconName.trim().length === 0 ? null : next.iconName.trim(),
    });
  };

  /**
   * Leaves edit mode and folds back what the server returned.
   *
   * Committing per keystroke means the row can be renamed by trimming the title to nothing, which the API refuses (400).
   * The refetch that follows such a refusal keeps the row as it was saved, and this sync restores the view when the
   * administrator clicks done — no half-typed value is left on screen as though it had been stored.
   */
  const stopEdit = () => {
    setEditDraft(categoryDraftOf(category));
    setEditing(false);
  };

  const hasChildren = category.subCategoryCount > 0;

  return (
    <>
      {editing ? (
        <>
          <CategoryFields draft={editDraft} onChange={changeEdit} isSaving={isSaving} />
          <div className="mt-3 flex flex-wrap gap-2">
            <Button type="button" size="sm" variant="outline" onClick={stopEdit}>
              <Check aria-hidden className="h-4 w-4" />
              {t('common.done')}
            </Button>
          </div>
        </>
      ) : (
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">{category.nameFa}</span>
            <span className="text-xs text-muted-foreground">{category.nameEn}</span>
            <Badge variant={category.isActive ? 'default' : 'outline'}>
              {category.isActive ? t('admin.categories.active') : t('admin.categories.inactive')}
            </Badge>
            {category.isDeleted ? (
              <Badge variant="secondary">{t('admin.categories.deleted')}</Badge>
            ) : null}
            <Badge variant="outline">
              {t('admin.categories.children', { count: category.subCategoryCount })}
            </Badge>
          </div>

          <p dir="ltr" className="font-mono text-xs text-muted-foreground">
            {category.slug} · {category.id}
          </p>
        </div>
      )}

      {editing ? null : (
        <CategoryActions
          category={category}
          expanded={expanded}
          hasChildren={hasChildren}
          onToggleExpanded={onToggleExpanded}
          onStartEdit={startEdit}
          onToggleState={onToggleState}
          isTogglingState={isTogglingState}
          onDelete={onDelete}
          isDeleting={isDeleting}
          onRestore={onRestore}
          isRestoring={isRestoring}
        />
      )}

      {/* The refusal is a rule of the API — nothing is cascaded — so the reason is stated once, under the row, rather
          than left to a failed request. */}
      {hasChildren && !category.isDeleted && !editing ? (
        <p id={`${category.id}-delete-blocked`} className="mt-2 text-xs text-muted-foreground">
          {t('admin.categories.deleteBlocked')}
        </p>
      ) : null}
    </>
  );
}

/**
 * The three editable fields of a category plus the read-only key it is addressed by.
 *
 * Every field commits as it is typed rather than behind a save button: once the row is in edit mode the draft *is* the
 * row, so a half-typed value is never stranded by clicking elsewhere. The only thing edit mode withholds is the slug.
 */
function CategoryFields({ draft, onChange, isSaving }: CategoryFieldsProps) {
  const { t } = useTranslation();

  return (
    <>
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-xs text-muted-foreground">{t('admin.categories.nameFa')}</span>
        <Input
          value={draft.nameFa}
          disabled={isSaving}
          aria-busy={isSaving}
          onChange={(event) => onChange({ ...draft, nameFa: event.target.value })}
        />
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-xs text-muted-foreground">{t('admin.categories.nameEn')}</span>
        <LtrInput
          value={draft.nameEn}
          disabled={isSaving}
          aria-busy={isSaving}
          onChange={(event) => onChange({ ...draft, nameEn: event.target.value })}
        />
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-xs text-muted-foreground">{t('admin.categories.order')}</span>
        <LtrInput
          inputMode="numeric"
          value={draft.order}
          disabled={isSaving}
          aria-busy={isSaving}
          onChange={(event) => onChange({ ...draft, order: event.target.value })}
        />
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-xs text-muted-foreground">{t('admin.categories.iconName')}</span>
        <LtrInput
          value={draft.iconName}
          disabled={isSaving}
          aria-busy={isSaving}
          onChange={(event) => onChange({ ...draft, iconName: event.target.value })}
        />
      </label>

      <p className="text-xs text-muted-foreground sm:col-span-2">
        {t('admin.categories.slugImmutable', { slug: draft.slug })}
      </p>
    </>
  );
}

/** Props accepted by {@link CategoryFields}. */
interface CategoryFieldsProps {
  /** Draft being edited. The slug member is shown, never edited. */
  draft: CategoryDraft;
  /** Replaces the whole draft; each call also sends the replacement to the API. */
  onChange: (draft: CategoryDraft) => void;
  /** True while the previous keystroke's request is still in flight. */
  isSaving?: boolean;
}

/** Props accepted by {@link CategoryActions}. */
interface CategoryActionsProps {
  /** Stored category the actions apply to. */
  category: AdminCategory;
  /** Whether the row's subcategories are open. */
  expanded: boolean;
  /** Whether the category still holds a live subcategory, which blocks its deletion. */
  hasChildren: boolean;
  /** Opens or closes the subcategories. */
  onToggleExpanded: () => void;
  /** Enters edit mode. */
  onStartEdit: () => void;
  /** Enables or disables the category. */
  onToggleState: () => void;
  /** Whether the state change is in flight. */
  isTogglingState: boolean;
  /** Soft-deletes the category. */
  onDelete: () => void;
  /** Whether the deletion is in flight. */
  isDeleting: boolean;
  /** Restores the category. */
  onRestore: () => void;
  /** Whether the restore is in flight. */
  isRestoring: boolean;
}

/** The lifecycle strip of one category row. A soft-deleted row offers restore alone, because nothing else applies. */
function CategoryActions({
  category,
  expanded,
  hasChildren,
  onToggleExpanded,
  onStartEdit,
  onToggleState,
  isTogglingState,
  onDelete,
  isDeleting,
  onRestore,
  isRestoring,
}: CategoryActionsProps) {
  const { t } = useTranslation();

  return (
    <div className="mt-3 flex flex-wrap gap-2">
      <Button
        type="button"
        size="sm"
        variant="ghost"
        aria-expanded={expanded}
        onClick={onToggleExpanded}
      >
        {expanded ? (
          <ChevronDown aria-hidden className="h-4 w-4" />
        ) : (
          <ChevronLeft aria-hidden className="h-4 w-4 rtl:rotate-180" />
        )}
        {t('admin.categories.subCategoriesLabel')}
      </Button>

      <Button type="button" size="sm" variant="outline" onClick={onStartEdit}>
        {t('common.edit')}
      </Button>

      {category.isDeleted ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={isRestoring}
          onClick={onRestore}
        >
          <RotateCcw aria-hidden className="h-4 w-4" />
          {t('admin.categories.restore')}
        </Button>
      ) : (
        <>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={isTogglingState}
            onClick={onToggleState}
          >
            {category.isActive ? t('admin.categories.disable') : t('admin.categories.enable')}
          </Button>

          <Button
            type="button"
            size="sm"
            variant="destructive"
            disabled={isDeleting || hasChildren}
            aria-describedby={hasChildren ? `${category.id}-delete-blocked` : undefined}
            onClick={onDelete}
          >
            <Trash2 aria-hidden className="h-4 w-4" />
            {t('admin.categories.delete')}
          </Button>
        </>
      )}
    </div>
  );
}

/** Props accepted by {@link SubCategoryPanel}. */
interface SubCategoryPanelProps {
  /** Parent category the panel edits. */
  categoryId: string;
  /** Whether the parent is soft-deleted; a deleted parent's children cannot be added to. */
  categoryDeleted: boolean;
}

/**
 * The subcategories of one expanded category.
 *
 * The list is read from the nested route (`…/categories/{id}/subcategories`), so it is scoped to the parent by the API as
 * well as by the key. A deleted parent still shows its children — so they can be restored — but offers no create form,
 * because a new row beneath a removed parent would be invisible to every picker.
 */
function SubCategoryPanel({ categoryId, categoryDeleted }: SubCategoryPanelProps) {
  const { t } = useTranslation();
  const messageFor = useProblemMessage();

  const subCategories = useAdminSubCategories(categoryId, true);
  const create = useCreateSubCategory();
  const update = useUpdateSubCategory();
  const setState = useSetSubCategoryState();
  const remove = useDeleteSubCategory();
  const restore = useRestoreSubCategory();

  const [draft, setDraft] = useState<SubCategoryDraft>(emptySubCategoryDraft);

  const rows = subCategories.data ?? [];

  const submitCreate = () => {
    create.mutate(
      {
        categoryId,
        payload: {
          slug: draft.slug.trim(),
          nameFa: draft.nameFa.trim(),
          nameEn: draft.nameEn.trim(),
          order: orderOf(draft.order),
          isActive: draft.isActive,
        },
      },
      { onSuccess: () => setDraft(emptySubCategoryDraft()) },
    );
  };

  const mutationError =
    create.error ?? update.error ?? setState.error ?? remove.error ?? restore.error ?? null;

  return (
    <div className="mt-3 flex flex-col gap-3 rounded-lg border border-dashed border-border p-3">
      <p className="text-sm font-medium">{t('admin.categories.subCategoriesTitle')}</p>

      {subCategories.isPending ? (
        <div className="flex justify-center p-2">
          <Spinner />
        </div>
      ) : null}

      {subCategories.isError ? (
        <Alert variant="destructive">{messageFor(subCategories.error)}</Alert>
      ) : null}
      {mutationError === null ? null : (
        <Alert variant="destructive">{messageFor(mutationError)}</Alert>
      )}

      {rows.length === 0 && !subCategories.isPending ? (
        <p className="text-sm text-muted-foreground">{t('admin.categories.subCategoriesEmpty')}</p>
      ) : null}

      <ul className="flex flex-col gap-2">
        {rows.map((subCategory) => (
          <li key={subCategory.id} className="rounded-lg border border-border p-3">
            <SubCategoryRow
              subCategory={subCategory}
              onSave={(payload) =>
                update.mutate({ categoryId, subCategoryId: subCategory.id, payload })
              }
              isSaving={update.isPending}
              onToggleState={() =>
                setState.mutate({
                  categoryId,
                  subCategoryId: subCategory.id,
                  isActive: !subCategory.isActive,
                })
              }
              isTogglingState={setState.isPending}
              onDelete={() => remove.mutate({ categoryId, subCategoryId: subCategory.id })}
              isDeleting={remove.isPending}
              onRestore={() => restore.mutate({ categoryId, subCategoryId: subCategory.id })}
              isRestoring={restore.isPending}
            />
          </li>
        ))}
      </ul>

      {categoryDeleted ? (
        <p className="text-xs text-muted-foreground">
          {t('admin.categories.subCategoriesParentDeleted')}
        </p>
      ) : (
        <div className="rounded-lg border border-border p-3">
          <p className="mb-3 flex items-center gap-2 text-sm font-medium">
            <Plus aria-hidden className="h-4 w-4" />
            {t('admin.categories.subCategoryCreateTitle')}
          </p>

          <SubCategoryFields draft={draft} onChange={setDraft} showSlug />

          <div className="mt-3">
            <Button
              type="button"
              size="sm"
              disabled={create.isPending || !isCreatable(draft.slug, draft.nameFa, draft.nameEn)}
              onClick={submitCreate}
            >
              <Plus aria-hidden className="h-4 w-4" />
              {t('admin.categories.subCategoryCreate')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Props accepted by {@link SubCategoryFields}. */
interface SubCategoryFieldsProps {
  /** Draft being edited. */
  draft: SubCategoryDraft;
  /** Replaces the whole draft; in the edit form each call also sends the replacement to the API. */
  onChange: (draft: SubCategoryDraft) => void;
  /** Whether the slug field is offered; only the create form does, because a slug never changes. */
  showSlug?: boolean;
  /** True while the previous keystroke's request is still in flight; ignored by the create form. */
  isSaving?: boolean;
}

/** The fields of a subcategory; the slug appears only where it can still be chosen. */
function SubCategoryFields({
  draft,
  onChange,
  showSlug = false,
  isSaving = false,
}: SubCategoryFieldsProps) {
  const { t } = useTranslation();

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {showSlug ? (
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-xs text-muted-foreground">{t('admin.categories.slug')}</span>
          <LtrInput
            value={draft.slug}
            placeholder="rest-api"
            onChange={(event) => onChange({ ...draft, slug: event.target.value })}
          />
          <span className="text-xs text-muted-foreground">
            {t('admin.categories.subCategorySlugHint')}
          </span>
        </label>
      ) : null}

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-xs text-muted-foreground">{t('admin.categories.nameFa')}</span>
        <Input
          value={draft.nameFa}
          disabled={isSaving}
          aria-busy={isSaving}
          onChange={(event) => onChange({ ...draft, nameFa: event.target.value })}
        />
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-xs text-muted-foreground">{t('admin.categories.nameEn')}</span>
        <LtrInput
          value={draft.nameEn}
          disabled={isSaving}
          aria-busy={isSaving}
          onChange={(event) => onChange({ ...draft, nameEn: event.target.value })}
        />
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-xs text-muted-foreground">{t('admin.categories.order')}</span>
        <LtrInput
          inputMode="numeric"
          value={draft.order}
          disabled={isSaving}
          aria-busy={isSaving}
          onChange={(event) => onChange({ ...draft, order: event.target.value })}
        />
      </label>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={draft.isActive}
          disabled={isSaving}
          onChange={(event) => onChange({ ...draft, isActive: event.target.checked })}
        />
        {t('admin.categories.activeLabel')}
      </label>
    </div>
  );
}

/** Props accepted by {@link SubCategoryRow}. */
interface SubCategoryRowProps {
  /** Stored subcategory the row renders. */
  subCategory: AdminSubCategory;
  onSave: (payload: { nameFa: string; nameEn: string; order: number }) => void;
  /** Whether an edit is in flight. */
  isSaving: boolean;
  /** Enables or disables the subcategory. */
  onToggleState: () => void;
  /** Whether the state change is in flight. */
  isTogglingState: boolean;
  /** Soft-deletes the subcategory. */
  onDelete: () => void;
  /** Whether the deletion is in flight. */
  isDeleting: boolean;
  /** Restores the subcategory. */
  onRestore: () => void;
  /** Whether the restore is in flight. */
  isRestoring: boolean;
}

/** One subcategory: its metadata, its edit form and the lifecycle actions that apply to it. */
function SubCategoryRow({
  subCategory,
  onSave,
  isSaving,
  onToggleState,
  isTogglingState,
  onDelete,
  isDeleting,
  onRestore,
  isRestoring,
}: SubCategoryRowProps) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [editDraft, setEditDraft] = useState<SubCategoryDraft>(() =>
    subCategoryDraftOf(subCategory),
  );

  const startEdit = () => {
    setEditDraft(subCategoryDraftOf(subCategory));
    setEditing(true);
  };

  /** Applies one field of the edit draft and sends the whole replacement, which is what the route accepts. */
  const changeEdit = (next: SubCategoryDraft) => {
    setEditDraft(next);
    onSave({
      nameFa: next.nameFa.trim(),
      nameEn: next.nameEn.trim(),
      order: orderOf(next.order, subCategory.order),
    });
  };

  /** Leaves edit mode and folds back what the server returned; the last keystroke was already sent. */
  const stopEdit = () => {
    setEditDraft(subCategoryDraftOf(subCategory));
    setEditing(false);
  };

  return (
    <>
      {editing ? (
        <SubCategoryFields draft={editDraft} onChange={changeEdit} isSaving={isSaving} />
      ) : (
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">{subCategory.nameFa}</span>
            <span className="text-xs text-muted-foreground">{subCategory.nameEn}</span>
            <Badge variant={subCategory.isActive ? 'default' : 'outline'}>
              {subCategory.isActive ? t('admin.categories.active') : t('admin.categories.inactive')}
            </Badge>
            {subCategory.isDeleted ? (
              <Badge variant="secondary">{t('admin.categories.deleted')}</Badge>
            ) : null}
          </div>

          <p dir="ltr" className="font-mono text-xs text-muted-foreground">
            {subCategory.slug} · {subCategory.id}
          </p>
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        {editing ? (
          <Button type="button" size="sm" variant="outline" onClick={stopEdit}>
            <Check aria-hidden className="h-4 w-4" />
            {t('common.done')}
          </Button>
        ) : (
          <SubCategoryActions
            subCategory={subCategory}
            onStartEdit={startEdit}
            onToggleState={onToggleState}
            isTogglingState={isTogglingState}
            onDelete={onDelete}
            isDeleting={isDeleting}
            onRestore={onRestore}
            isRestoring={isRestoring}
          />
        )}
      </div>
    </>
  );
}

/** Props accepted by {@link SubCategoryActions}. */
interface SubCategoryActionsProps {
  /** Stored subcategory the actions apply to. */
  subCategory: AdminSubCategory;
  /** Enters edit mode. */
  onStartEdit: () => void;
  /** Enables or disables the subcategory. */
  onToggleState: () => void;
  /** Whether the state change is in flight. */
  isTogglingState: boolean;
  /** Soft-deletes the subcategory. */
  onDelete: () => void;
  /** Whether the deletion is in flight. */
  isDeleting: boolean;
  /** Restores the subcategory. */
  onRestore: () => void;
  /** Whether the restore is in flight. */
  isRestoring: boolean;
}

/**
 * The lifecycle strip of one subcategory row.
 *
 * Unlike a category, a subcategory carries no children, so its deletion is never refused: the strip always offers it.
 */
function SubCategoryActions({
  subCategory,
  onStartEdit,
  onToggleState,
  isTogglingState,
  onDelete,
  isDeleting,
  onRestore,
  isRestoring,
}: SubCategoryActionsProps) {
  const { t } = useTranslation();

  return (
    <>
      <Button type="button" size="sm" variant="outline" onClick={onStartEdit}>
        {t('common.edit')}
      </Button>

      {subCategory.isDeleted ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={isRestoring}
          onClick={onRestore}
        >
          <RotateCcw aria-hidden className="h-4 w-4" />
          {t('admin.categories.restore')}
        </Button>
      ) : (
        <>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={isTogglingState}
            onClick={onToggleState}
          >
            {subCategory.isActive ? t('admin.categories.disable') : t('admin.categories.enable')}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="destructive"
            disabled={isDeleting}
            onClick={onDelete}
          >
            <Trash2 aria-hidden className="h-4 w-4" />
            {t('admin.categories.delete')}
          </Button>
        </>
      )}
    </>
  );
}
